import React, {useEffect, useMemo, useState} from 'react';
import {
  Flex,
  Row,
  Space,
  Table,
  Select,
  Col,
  Layout,
  Typography,
  theme,
  DatePicker,
  Button,
  Dropdown, TableProps, Popover,
} from "antd";
import type {TableColumnsType} from 'antd';
import type {DefaultOptionType} from 'antd/es/select';
import type { PaginationProps } from 'antd';
import ButtonGreen from "../../components/ui/buttons/ButtonGreen";
import Information from "../../components/information";
import IconPlus from "../../components/ui/icons/IconPlus";
import useSWR from "swr";
import urls from "../../api/urls"
import {api, HydraData} from "../../api/apiProvider";
import {Link, NavLink, useNavigate, useSearchParams} from "react-router-dom";
import {debounce} from "lodash";
import dayjs, {Dayjs} from "dayjs";
import IconClose from "../../components/ui/icons/IconClose";
import {t} from "i18next";
import {useListActionItems} from "./useListActionItems";
import Protected from "../../roles/Protected";
import Roles from "../../roles/Roles";
import LayoutSider from "../../components/brief/LayoutSider";
import {ModelValidationResult} from "../../api/types";
import IconAchtung from "../../components/ui/icons/IconAchtung";
import {usePage} from "../../hooks/usePage";

const {Title} = Typography;
const {Header, Content} = Layout;
const {RangePicker} = DatePicker;


const dateFormat = 'YYYY-MM-DD';

interface SortFields {
  pool: string,
  company: string,
  products: string,
  kpi: string,
  version: [string, string],
}

const sortFields: SortFields = {
  pool: 'products.company.pool.name',
  company: 'products.company.name',
  products: 'products.name',
  kpi: 'kpi.name',
  version: ['versionMajor', 'versionMinor'],
};

type OnChange = NonNullable<TableProps<DataType>['onChange']>
type GetSingle<T> = T extends (infer U)[] ? U : never
type Sorts = GetSingle<Parameters<OnChange>[2]>

interface Model {
  id: string,
  '@id'?: string,
  name: string,
}

interface Company {
  '@id': string,
  id: number,
  name: string,
  pool: Model,

}

interface Product {
  company: Company,
  id: number,
  mpmId: string,
  name: string,
}

interface BriefFilter {
  company?: UserValue,
  pool?: UserValue,
  products?: UserValue[],
  period?: [Dayjs | null, Dayjs | null] | null
}

interface DataType {
  id: string;
  name: string;
  products: Product[];
  kpi: Model;
  seasonalityText: string;
  version: string;
  //channels: BriefChannel[];
  efficiencyMetrics: Model[];
  dataDateStart: string;
  dataDateEnd: string;
  validationDeadline: string;
  commentary: string;
  setupValidation: boolean;
  isAllowed: boolean;
  result: ModelValidationResult;
}

interface DataResourceType {
  id: string;
  name: string;
  products: string[];
  company: string;
  pool: string;
  kpi: string;
  version: string;
  seasonalityText: string;
  //channels: string[];
  efficiencyMetrics: string[];
  period: string;
  validationDeadline: string;
  setupValidation: boolean;
  isAllowed: boolean;
  result: ModelValidationResult;
}

interface UserValue {
  label?: string;
  value?: string;
}

interface ListProps {
  isActual: boolean,
}

export default function List({isActual}: ListProps) {
  const [selectedBriefId, setSelectedBriefId] = useState(0);
  const [companySearch, setCompanySearch] = useState<string>('');
  const [poolSearch, setPoolSearch] = useState<string>('');
  const [productSearch, setProductSearch] = useState<string>('');
  const [briefFilter, setBriefFilter] = useState<BriefFilter>({});
  const [companiesOptions, setCompaniesOptions] = useState<DefaultOptionType[]>([]);
  const [productsOptions, setProductsOptions] = useState<DefaultOptionType[]>([]);
  const [poolsOptions, setPoolsOptions] = useState<DefaultOptionType[]>([]);
  const [sorter, setSorter] = useState<Sorts>({});
  const navigate = useNavigate();
  const [total, setTotal] = useState<number>(0);
  const {page, setCurrentPage} = usePage(isActual);

  const onPageChange: PaginationProps['onChange'] = (page) => {
    setCurrentPage(page);
    setSelectedBriefId(0);
    navigate({
      search: ''
    })
  };

  const [searchParams] = useSearchParams();

  const {
    token: {borderRadiusLG},
  } = theme.useToken();

  const getBriefsParamsString = () => {
    const params = new URLSearchParams([
      ['t[]', 'brief'], ['t[]', 'brief:metric'], ['t[]', 'dict:product'], ['t[]', 'product:company'],
      ['t[]', 'company'], ['t[]', 'company:pool'], ['t[]', 'pool'], ['t[]', 'dict'], ['t[]', 'dict:efficiencyMetric'],
    ]);

    if (briefFilter.pool) {
      params.append('products.company.pool.id', String(briefFilter.pool.value))
    }

    if (briefFilter.company) {
      params.append('products.company.id', String(briefFilter.company.value))
    }

    if (briefFilter.products) {
      for (let product of briefFilter.products) {
        params.append('products.id[]', String(product.value))
      }
    }

    if (briefFilter.period && briefFilter.period.length > 0) {
      if (briefFilter.period[0] && briefFilter.period[1]) {
        params.append('dataDateStart[after]', briefFilter.period[0].format(dateFormat));
        params.append('dataDateEnd[before]', briefFilter.period[1].format(dateFormat))
      }
    }

    if (sorter && sorter.column) {
      const order = sorter.order === 'ascend' ? 'asc' : 'desc';

      if (Array.isArray(sortFields[String(sorter.columnKey) as keyof SortFields])) {
        for (let key of sortFields[String(sorter.columnKey) as keyof SortFields]) {
          params.append(
            `order[${key}]`,
            order,
          )
        }
      } else {
        params.append(
          `order[${sortFields[String(sorter.columnKey) as keyof SortFields]}]`,
          order,
        )
      }
    }

    params.append('page', String(page));

    return params.toString()
  };

  const {
    data: briefs,
    mutate,
    isValidating
  } = useSWR<{
    data: DataType[],
    total: Number,
  }>(`${urls.briefs}?isActive=${isActual}&${getBriefsParamsString()}`, api.get, {
    revalidateOnFocus: false
  });

  const {getActualItems, getNotActualItems} = useListActionItems(mutate);

  const {
    data: pools,
  } = useSWR<HydraData>(poolSearch ? `${urls.pools}?name=${poolSearch}&pagination=false` : `${urls.pools}?pagination=false`, api.get, {
    revalidateOnFocus: false
  });

  const lessThanThreeMonthsLeft = (item: DataResourceType) => {
    if (!item.validationDeadline) {
      return false
    }

    const validatedDate = dayjs(item.validationDeadline).subtract(3, 'month');
    return validatedDate.isBefore(dayjs())
  };

  const columns: TableColumnsType<DataResourceType> = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      render: (value: any, record: DataResourceType, index: number) => {
        return isActual
          ? <Flex gap={'small'} align={'center'}>
            <Link to={{pathname: `/briefs/${record.id}/description`}}>
              {value}
            </Link>
            {lessThanThreeMonthsLeft(record) &&
              <Popover placement="topRight" content={
                <div>{t('brief.lessThanThreeMonths')}</div>
              } arrow={false}>
                <Flex align={'center'} gap={'small'}>
                  <IconAchtung/>
                </Flex>
              </Popover>
            }
          </Flex>
          : value
      },
    },
    {
      title: t('brief.pool'),
      dataIndex: 'pool',
      key: 'pool',
      width: 200,
      sorter: true,
    },
    {
      title: t('brief.company'),
      dataIndex: 'company',
      key: 'company',
      width: 200,
      sorter: true,
    },
    {
      title: t('brief.product'),
      dataIndex: 'products',
      key: 'products',
      width: 200,
      render: (value: any, record: DataResourceType, index: number) => {
        return value.map((item: string) => <div key={item}>{item}</div>)
      },
      sorter: true,
    },
    {
      title: 'KPI',
      dataIndex: 'kpi',
      key: 'kpi',
      width: 150,
      sorter: true,
    },
    {
      title: t('list.version'),
      dataIndex: 'version',
      key: 'version',
      width: 100,
      sorter: true,
    },
    {
      title: t('brief.efficiencyMetrics'),
      dataIndex: 'efficiencyMetrics',
      key: 'efficiencyMetrics',
      width: 150,
      render: (value: any, record: DataResourceType, index: number) => {
        return value.map((item: string) => <div key={item}>{item}</div>)
      },
    },
    {
      title: t('brief.period'),
      dataIndex: 'period',
      key: 'period',
      width: 350,
    },
    {
      title: t('brief.setupValidation'),
      dataIndex: 'setupValidation',
      key: 'setupValidation',
      width: 130,
      fixed: 'right',
      render: (value: any, record: DataResourceType, index: number) => {
        return <div>{value
          ? (record.validationDeadline
            ? <div className={'briefs-table__validated_date'}>
              {`${dayjs(record.validationDeadline).format('DD.MM.YYYY')}`}
              </div>
            : t('list.withoutDate'))
          : t('list.withoutValidation')}
        </div>
      },
    },
    {
      title: t('brief.isAllowed'),
      dataIndex: 'isAllowed',
      key: 'isAllowed',
      width: 100,
      fixed: 'right',
      render: (value: any, record: DataResourceType, index: number) => {
        return value ? t('common.yes') : t('common.no')
      },
    },
  ];

  if (Roles.hasAccess('canCreateOrUpdateBrief')) {
    columns.push({
      title: '',
      dataIndex: 'actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (value: any, record: DataResourceType, index: number) => {
        return isActual ? <Dropdown
            menu={{items: getActualItems(Number(record.id))}}
            placement="bottom"
            arrow
            trigger={['click', 'hover']}
          >
            <Button size={'small'} onClick={() => {
            }}>...</Button>
          </Dropdown>
          : <Dropdown
            menu={{items: getNotActualItems(Number(record.id))}}
            placement="bottom"
            arrow
            trigger={['click', 'hover']}
          >
            <Button size={'small'} onClick={() => {
            }}>...</Button>
          </Dropdown>
      },
    })
  }

  const getCompaniesParamsString = () => {
    const params = new URLSearchParams();

    if (companySearch) {
      params.append('name', companySearch)
    }

    if (briefFilter.pool) {
      params.append('pool.id', String(briefFilter.pool.value))
    }

    params.append('pagination', String(false));

    return params.toString()
  };

  const {
    data: companies,
  } = useSWR<HydraData>(`${urls.companies}?${getCompaniesParamsString()}`, api.get, {
    revalidateOnFocus: false
  });

  const getProductsParamsString = () => {
    const params = new URLSearchParams();

    if (productSearch) {
      params.append('name', productSearch)
    }

    if (briefFilter.company) {
      params.append('company.id', String(briefFilter.company.value))
    }

    params.append('pagination', String(false));

    return params.toString()
  };

  const {
    data: products,
  } = useSWR<HydraData>(`${urls.products}?${getProductsParamsString()}`, api.get, {
    revalidateOnFocus: false
  });

  useEffect(() => {
    const briefId = searchParams.get('id');
    if (briefId && Roles.hasAccess('canReadBriefDetails')) {
      setSelectedBriefId(Number(briefId))
    }
  }, []);

  useEffect(() => {
    if (selectedBriefId) {
      navigate({
        search: `id=${selectedBriefId}`
      })
    }
  }, [selectedBriefId]);

  useEffect(() => {
    if (companies && companies.data) {
      setCompaniesOptions(companies.data.map((company: Model) => {
        return {
          value: company.id,
          label: company.name,
        }
      }))
    }
  }, [companies]);

  useEffect(() => {
    if (pools && pools.data) {
      setPoolsOptions(pools.data.map((pool: Model) => {
        return {
          value: pool.id,
          label: pool.name,
        }
      }))
    }
  }, [pools]);

  useEffect(() => {
    if (products && products.data) {
      setProductsOptions(products.data.map((product: Model) => {
        return {
          value: product.id,
          label: product.name,
        }
      }))
    }
  }, [products]);

  const data = useMemo(() => {
    const data: DataResourceType[] = [];

    if (!briefs || !briefs.data) {
      return []
    }

    for (let brief of briefs.data as DataType[]) {
      const product = brief.products[0] ?? {};
      data.push({
        id: brief.id,
        name: brief.name,
        products: brief.products
        .map(product => product.name as string),
        company: product.company ? product.company.name : '',
        pool: product.company ? product.company.pool.name : '',
        setupValidation: brief.setupValidation,
        validationDeadline: brief.validationDeadline,
        isAllowed: brief.isAllowed,
        seasonalityText: brief.seasonalityText,
        kpi: brief.kpi && brief.kpi.name ? brief.kpi.name : '',
        version: brief.version || '',
        result: brief.result,
        /*channels: brief.channels
          .map(channel => channel.channel.name as string),*/
        efficiencyMetrics: brief.efficiencyMetrics
        .map(efficiencyMetric => efficiencyMetric.name),
        period: `${dayjs(brief.dataDateStart).format('DD.MM.YYYY')} — ${dayjs(brief.dataDateEnd).format('DD.MM.YYYY')}`,
      })
    }

    return data
  }, [briefs]);

  useEffect(() => {
    if (briefs && briefs.total) {
      setTotal(Number(briefs.total))
    }
  }, [briefs]);

  const rowSelection = {
    selectedRowKeys: [String(selectedBriefId)],
    hideSelectAll: true,
    columnWidth: 0,
  };

  const onRowDoubleClick = (record: DataResourceType) => {
    navigate({
      pathname: `/briefs/${record.id}/description`
    })
  };

  const getSelectedBriefCommentaryId = () => {
    if (!selectedBriefId || !briefs || !briefs.data) {
      return 0
    }

    const selectedBrief = briefs.data.find((brief: DataType) => Number(brief.id) === selectedBriefId);

    if (!selectedBrief) {
      return 0
    }

    return Number(selectedBrief.commentary.split('/').pop())
  };

  const onChange: TableProps<DataResourceType>['onChange'] = (pagination, filters, sorter, extra) => {
    setSorter(sorter as Sorts)
  };

  const getRowClassName = (item: any) => {
    let classes = '';

    if (selectedBriefId === Number(item.id)) {
      classes += 'briefs-table__selected'
    }

    if (item.result && item.result.code) {
      classes += ' briefs-table__color-' + item.result.code
    }

    return classes
  };

  return (
    <Layout>
      <Header className={'layout-header'}>
        <Row justify={"space-between"} style={{flex: 1}} align={"middle"}>
          <Col span={8}></Col>
          <Col span={8}>
            <Flex justify={'center'}><Title level={5} style={{margin: 0}}>{t('list.title')}</Title></Flex>
          </Col>
          <Col span={8}></Col>
        </Row>
      </Header>
      <Layout style={{paddingLeft: '48px'}}>
        <LayoutSider/>
        <Layout>
          <Content
            style={{
              margin: 0,
              minHeight: 280,
              background: '#F1F5F7',
              borderRadius: borderRadiusLG,
            }}
          >
            <div className={'page-wrapper'}>
              <section className={'main-section'}>
                <Space direction="vertical" size={12} style={{width: '100%'}}>
                  <Flex className={'list-tabs'}>
                    <NavLink to={{pathname: '/briefs/actual'}} className={'list-tabs__tab'}>{t('list.actual')}
                      {(briefs && briefs.total > 0 && isActual) &&
                        <> ({briefs.total})</>
                      }
                    </NavLink>
                    <NavLink to={{pathname: '/briefs/archived'}} className={'list-tabs__tab'}>{t('list.theArchived')}
                      {(briefs && briefs.total > 0 && !isActual) &&
                        <> ({briefs.total})</>
                      }
                    </NavLink>
                  </Flex>
                  <Row>
                    <Flex justify={"space-between"} style={{width: '100%'}}>
                      <Flex gap={'small'}>
                        <Select
                          style={{width: 200}}
                          showSearch
                          allowClear
                          labelInValue
                          placeholder={t('brief.pool')}
                          optionFilterProp="children"
                          onChange={(data: UserValue) => {
                            delete briefFilter.company;
                            delete briefFilter.products;
                            setBriefFilter({
                              ...briefFilter,
                              pool: data,
                            })
                          }}
                          onSearch={debounce((value: string) => {
                            setPoolSearch(value)
                          }, 600)}
                          filterOption={false}
                          options={poolsOptions}
                          value={briefFilter.pool}
                        />
                        <Select
                          style={{width: 200}}
                          showSearch
                          allowClear
                          labelInValue
                          placeholder={t('brief.company')}
                          optionFilterProp="children"
                          onChange={(data: UserValue) => {
                            delete briefFilter.products;
                            setBriefFilter({
                              ...briefFilter,
                              company: data,
                            })
                          }}
                          onSearch={debounce((value: string) => setCompanySearch(value), 600)}
                          filterOption={false}
                          options={companiesOptions}
                          value={briefFilter.company}
                        />
                        <Select
                          style={{width: 200}}
                          showSearch
                          allowClear
                          labelInValue
                          placeholder={t('brief.product')}
                          optionFilterProp="children"
                          mode="multiple"
                          onChange={(data: UserValue[]) => {
                            setBriefFilter({
                              ...briefFilter,
                              products: data,
                            })
                          }}
                          onSearch={debounce((value: string) => setProductSearch(value), 600)}
                          filterOption={false}
                          options={productsOptions}
                          value={briefFilter.products}
                        />
                        <RangePicker
                          placeholder={[t('list.dateStart'), t('list.dateEnd')]}
                          onChange={(date) => {
                            if (date) {
                              setBriefFilter({
                                ...briefFilter,
                                period: date,
                              })
                            } else {
                              setBriefFilter({
                                ...briefFilter,
                                period: null,
                              })
                            }
                          }}
                          format={'DD.MM.YYYY'}
                        />
                      </Flex>
                      <Protected access={'canCreateOrUpdateBrief'}>
                        <ButtonGreen onClick={() => navigate({
                          pathname: '/briefs/0/description'
                        })}>
                          <Flex gap={'small'} align={'center'}>
                            <IconPlus/>
                            <div className={'text--strong'}>{t('list.create')}</div>
                          </Flex>
                        </ButtonGreen>
                      </Protected>
                    </Flex>
                  </Row>
                  <Row>
                    <Table
                      className={'briefs-table'}
                      columns={columns}
                      dataSource={data}
                      rowKey={'id'}
                      scroll={{x: 800}}
                      size={'middle'}
                      loading={isValidating}
                      rowSelection={rowSelection}
                      onRow={(record, rowIndex) => {
                        return {
                          onClick: (event) => {
                            if (!Roles.hasAccess('canReadBriefDetails')) {
                              return
                            }

                            if (event.target instanceof HTMLElement) {
                              if (event.target && event.target.nodeName !== 'TD') {
                                event.stopPropagation();
                                return
                              }
                            }

                            if (Number(record.id) === selectedBriefId) {
                              setSelectedBriefId(0);
                              navigate({
                                search: ''
                              })
                            } else {
                              setSelectedBriefId(Number(record.id))
                            }
                          },
                          onDoubleClick: () => onRowDoubleClick(record),
                        };
                      }}
                      rowClassName={(record) => getRowClassName(record)}
                      onChange={onChange}
                      pagination={{
                        current: page,
                        onChange: onPageChange,
                        total: total || 0,
                        pageSize: 30,
                      }}
                    />
                  </Row>
                </Space>
              </section>
              {selectedBriefId > 0 && Roles.hasAccess('canReadBriefDetails') &&
                <section className={'side-section'}>
                  <div className={'side-section__close'} onClick={() => setSelectedBriefId(0)}>
                    <IconClose/>
                  </div>
                  <Information
                    briefId={selectedBriefId}
                    updateList={mutate}
                    commentaryId={getSelectedBriefCommentaryId()}
                    showStages={true}
                  />
                </section>
              }
            </div>
          </Content>
        </Layout>
      </Layout>
    </Layout>
  )
}
